import { FileSpreadsheet, FileText, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatFileSize, getFileExtension, isExcelFile } from '../utils/fileValidation';

export type ValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid';

export interface FilePreviewProps {
  file: File;
  onRemove: () => void;
  validationStatus?: ValidationStatus;
  validationError?: string;
  questionsCount?: number;
  disabled?: boolean;
}

export function FilePreview({
  file,
  onRemove,
  validationStatus = 'idle',
  validationError,
  questionsCount,
  disabled,
}: FilePreviewProps) {
  const isExcel = isExcelFile(file);
  const FileIcon = isExcel ? FileSpreadsheet : FileText;
  const iconColor = isExcel ? 'text-green-600' : 'text-blue-600';

  const renderStatus = () => {
    switch (validationStatus) {
      case 'validating':
        return (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Parsing file...</span>
          </div>
        );
      case 'valid':
        return (
          <div className="flex items-center gap-2 text-tier-exact">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm">
              {questionsCount !== undefined
                ? `${questionsCount} question${questionsCount !== 1 ? 's' : ''} found`
                : 'File validated'}
            </span>
          </div>
        );
      case 'invalid':
        return (
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{validationError || 'Invalid file'}</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        'border rounded-lg p-4 transition-colors',
        validationStatus === 'valid' && 'border-tier-exact bg-tier-exact/5',
        validationStatus === 'invalid' && 'border-destructive bg-destructive/5',
        validationStatus === 'validating' && 'border-muted-foreground/25',
        validationStatus === 'idle' && 'border-muted-foreground/25'
      )}
    >
      <div className="flex items-start gap-4">
        <div className={cn('p-2 rounded-lg bg-muted', iconColor)}>
          <FileIcon className="h-8 w-8" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium truncate">{file.name}</p>
              <p className="text-sm text-muted-foreground">
                {formatFileSize(file.size)} • {getFileExtension(file.name).toUpperCase().slice(1)}
              </p>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={onRemove}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-2">{renderStatus()}</div>
        </div>
      </div>
    </div>
  );
}
